import { ArrowDownIcon } from '@synergy/theme-next';
import {
    Box,
    FormControl,
    FormHelperText,
    FormHelperTextProps,
    InputLabel,
    MenuItem,
    Select,
    SelectProps,
} from '@mui/material';
import { ReactElement, ReactNode } from 'react';

export interface PoolSelectOption<T = number> {
    value: T | '';
    displayValue: ReactNode;
}

export type PoolSelectProps<T = unknown> = SelectProps<T | ''> & {
    options: PoolSelectOption<T>[];
    helperText?: ReactNode;
    hint?: ReactNode;
    FormHelperTextProps?: FormHelperTextProps;
    placeholder?: string;
};

/**
 * PoolSelect<br>
 * <a href="https://www.figma.com/file/kjfAT06qgLdW52cF1SqWuy/Pool-Alpin-Shop-2.0-Library?node-id=721%3A10055" target="_blank">Open in Figma</a>
 */
export const PoolSelect = <T,>(props: PoolSelectProps<T>): ReactElement => {
    const {
        required,
        error,
        options,
        helperText,
        hint,
        value,
        FormHelperTextProps,
        placeholder,
        children,
        ...selectProps
    } = props;
    const { fullWidth, label, labelId } = selectProps;

    const renderValue = (value: T | ''): ReactNode => {
        if (value === '') {
            return null;
        }

        const displayValue = options.find(option => option.value === value)?.displayValue;
        if (displayValue) {
            return displayValue;
        }

        return value as unknown as ReactNode;
    };

    const formHelperText = helperText && error ? helperText : hint;

    return (
        <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth={fullWidth} sx={{ minWidth: 120 }} error={error} required={required}>
                <InputLabel id={labelId}>{label}</InputLabel>
                <Select<T | ''>
                    renderValue={renderValue}
                    IconComponent={ArrowDownIcon}
                    value={value ?? ''}
                    inputProps={{
                        placeholder,
                    }}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                                overflow: 'auto',
                            },
                        },
                    }}
                    {...selectProps}
                >
                    {children ??
                        options?.map((option, index) => (
                            <MenuItem key={index} value={option.value as unknown as string}>
                                {option.displayValue}
                            </MenuItem>
                        ))}
                </Select>
                <FormHelperText error={error} {...FormHelperTextProps}>
                    {formHelperText}
                </FormHelperText>
            </FormControl>
        </Box>
    );
};
